// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo } from 'react';

import { roomScopeGroups, type RoomInfo } from '../../bygning/rooms';
import type { Storey } from '../../bygning/storeys';
import { Select } from '../Select';

export interface RoomScopeProps {
  /** The building's rooms (`roomsOf`); only those with an id are offered. */
  rooms: readonly RoomInfo[];
  /** The storeys to group them by (`useStoreys`); none = a flat list. */
  storeys: readonly Storey[];
  /** The room in focus (`?room=`); null = hele bygningen. */
  value: number | null;
  onChange: (room: number | null) => void;
  /** Fill the container's width (the left tile). */
  block?: boolean;
  className?: string;
}

/**
 * The room focus as a picker — first in each of Ressourcer's toolbars and in
 * the Model's left tile: "Hele bygningen", then the rooms grouped by storey
 * ("Andre rum" for those on none). It sets `?room=` only: the storey focus
 * is left alone, as a click on a room in the model does. The crumb stays the
 * way back out on desktop.
 */
export function RoomScope({ rooms, storeys, value, onChange, block, className }: RoomScopeProps) {
  const groups = useMemo(() => roomScopeGroups(rooms, storeys), [rooms, storeys]);
  // A focused room the survey files no part in (only `GET /rooms` knows it) still shows as chosen.
  const listed = value === null || groups.some((g) => g.rooms.some((r) => r.id === value));
  const option = (r: RoomInfo) => (
    <option key={r.key} value={String(r.id)}>
      {r.name}
    </option>
  );
  return (
    <Select
      variant="filled"
      size="md"
      block={block}
      className={className}
      aria-label="Rum"
      value={value === null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    >
      <option value="">Hele bygningen</option>
      {!listed && <option value={String(value)}>{`Rum ${value}`}</option>}
      {groups.map((g) =>
        g.label === null ? (
          g.rooms.map(option)
        ) : (
          <optgroup key={g.label} label={g.label}>
            {g.rooms.map(option)}
          </optgroup>
        ),
      )}
    </Select>
  );
}
